// components/ui/DropdownWithLabel/ProductTypeDropdown.tsx
import React from 'react';
import DropdownWithLabel from '@/components/ui/DropdownWithLabel';
import type { ProductType } from '@/types/productTypes.types';
import { useGetProductTypesQuery } from "@/components/features/productTypes/services/productTypes.queries";

interface ProductTypeDropdownProps {
    label?: string;
    value?: string;
    onChange?: (value: string) => void;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    helperText?: string;
}

const ProductTypeDropdown = React.forwardRef<HTMLSelectElement, ProductTypeDropdownProps>(
    ({
         label = 'Τύπος Προϊόντος',
         value,
         onChange,
         error,
         required = false,
         disabled = false,
         placeholder = 'Επιλέξτε τύπο προϊόντος',
         helperText,
         ...props
     }, ref) => {
        const { data: productTypes, isLoading, isError } = useGetProductTypesQuery();
        const [currentValue, setCurrentValue] = React.useState<string>('');

        React.useEffect(() => {
            // When value prop changes or when productTypes load
            if (value) {
                setCurrentValue(value);
            } else {
                setCurrentValue('');
            }
        }, [value, productTypes]);

        const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
            const newValue = event.target.value;
            setCurrentValue(newValue);
            onChange?.(newValue);
        };

        const productTypeOptions = React.useMemo(() => {
            if (!productTypes) return [];

            return [
                { value: '', label: placeholder },
                ...productTypes.map((type: ProductType) => ({
                    value: type.product_type_id,
                    label: type.product_type_name,
                }))
            ];
        }, [productTypes, placeholder]);

        if (isError) {
            return (
                <DropdownWithLabel
                    ref={ref}
                    label={label}
                    options={[]}
                    error="Σφάλμα φόρτωσης τύπων προϊόντων"
                    disabled
                    required={required}
                    {...props}
                />
            );
        }

        return (
            <DropdownWithLabel
                ref={ref}
                label={label}
                options={productTypeOptions}
                value={currentValue}
                onChange={handleChange}
                error={error}
                disabled={disabled || isLoading}
                required={required}
                helperText={helperText}
                {...props}
            />
        );
    }
);

ProductTypeDropdown.displayName = 'ProductTypeDropdown';

export default ProductTypeDropdown;
